import isEmpty from 'lodash/isEmpty';
import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';

import { PersonOrgType } from '@ndvr/customer-api';

export enum ClientFilterKeys {
    personOrgTypes = 'personOrgTypes',
    states = 'states',
    // Sorry could not come up with better variable name
    // `startDate` is the original variable name that we need to range filter
    startDateStart = 'startDateStart',
    startDateEnd = 'startDateEnd',
}

export interface ClientFilters {
    personOrgTypes: PersonOrgType[];
    states: string[];
    startDateStart: string | null;
    startDateEnd: string | null;
}

const CLIENT_FILTERS_DEFAULT: ClientFilters = {
    personOrgTypes: [],
    states: [],
    startDateStart: null,
    startDateEnd: null,
};

export interface ClientFiltersCtx {
    filters: ClientFilters;
    patchFilters: (partialFilters: Partial<ClientFilters>) => void;
    resetFilters: (clientFilterKeys?: ClientFilterKeys[]) => void;
    isFilterActive: boolean;
    personOrgTypeFilterCount: number;
    isPersonOrgTypeFilterActive: boolean;
    statesFilterCount: number;
    isStatesFilterActive: boolean;
    startDateFilterCount: number;
    isStartDateFilterActive: boolean;
}

const ClientFiltersContext = createContext<ClientFiltersCtx>({
    filters: CLIENT_FILTERS_DEFAULT,
    patchFilters: () => {},
    resetFilters: () => {},
    isFilterActive: false,
    personOrgTypeFilterCount: 0,
    isPersonOrgTypeFilterActive: false,
    statesFilterCount: 0,
    isStatesFilterActive: false,
    startDateFilterCount: 0,
    isStartDateFilterActive: false,
});

const FILTERS_LOCAL_STORAGE_KEY = 'clients_active_filters';

export const ClientFiltersContextProvider = ({ children }: PropsWithChildren) => {
    const [filters, setFilters] = useState<ClientFilters>(CLIENT_FILTERS_DEFAULT);

    useEffect(() => {
        const filterStringified = globalThis.localStorage.getItem(FILTERS_LOCAL_STORAGE_KEY);
        if (filterStringified) {
            const localStorageFilters = JSON.parse(filterStringified);
            setFilters((filtersState) => ({ ...filtersState, ...localStorageFilters }));
        }
    }, []);

    useEffect(() => {
        globalThis.localStorage.setItem(FILTERS_LOCAL_STORAGE_KEY, JSON.stringify(filters));
    }, [filters]);

    const patchFilters = (partialFilters: Partial<ClientFilters>) =>
        setFilters((filtersState) => ({ ...filtersState, ...partialFilters }));

    const resetFilters = (filterKeysToReset: ClientFilterKeys[] = Object.values(ClientFilterKeys)) => {
        const filterDefaults: Partial<ClientFilters> = filterKeysToReset.reduce(
            (acc, filterKey: ClientFilterKeys) => ({
                ...acc,
                [filterKey]: CLIENT_FILTERS_DEFAULT[filterKey],
            }),
            {},
        );
        setFilters((filterState) => ({ ...filterState, ...filterDefaults }));
    };

    const personOrgTypeFilterCount = useMemo(() => filters.personOrgTypes.length, [filters.personOrgTypes]);

    const isPersonOrgTypeFilterActive = useMemo(() => 0 < personOrgTypeFilterCount, [personOrgTypeFilterCount]);

    const statesFilterCount = useMemo(() => filters.states.length, [filters.states]);

    const isStatesFilterActive = useMemo(() => 0 < statesFilterCount, [statesFilterCount]);

    const isStartDateFilterActive = useMemo(
        () => !isEmpty(filters.startDateStart) || !isEmpty(filters.startDateEnd),
        [filters.startDateStart, filters.startDateEnd],
    );

    const startDateFilterCount = isStartDateFilterActive ? 1 : 0;

    const isFilterActive = useMemo(
        () => isPersonOrgTypeFilterActive || isStatesFilterActive || isStartDateFilterActive,
        [isPersonOrgTypeFilterActive, isStatesFilterActive, isStartDateFilterActive],
    );

    return (
        <ClientFiltersContext.Provider
            value={{
                filters,
                patchFilters,
                resetFilters,
                isFilterActive,
                personOrgTypeFilterCount,
                isPersonOrgTypeFilterActive,
                statesFilterCount,
                isStatesFilterActive,
                isStartDateFilterActive,
                startDateFilterCount,
            }}
        >
            {children}
        </ClientFiltersContext.Provider>
    );
};

export const useClientFiltersContext = () => {
    const context = useContext(ClientFiltersContext);
    if (!context) {
        throw Error('useClientFiltersContext must be used within ClientFiltersContextProvider');
    }
    return context;
};
